import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { assessmentsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

export default function AssessmentListPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => { loadAssessments(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  // Auto-refresh while any assessment is generating
  useEffect(() => {
    const hasGenerating = assessments.some(a => a.status === 'generating');
    if (!hasGenerating) return;
    const iv = setInterval(loadAssessments, 5000);
    return () => clearInterval(iv);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessments]);

  const loadAssessments = async () => {
    try {
      const res = await assessmentsAPI.listForJob(jobId);
      setAssessments(res.data);
    } catch (err) {
      toast.error('Failed to load assessments');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await assessmentsAPI.generate(jobId, 10);
      toast.success('Assessment is being generated and answer-key verified by AI. This may take 45-90 seconds...');
      loadAssessments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate assessment');
    } finally {
      setGenerating(false);
    }
  };

  const copyLink = (code) => {
    const url = `${window.location.origin}/take-assessment/${code}`;
    navigator.clipboard.writeText(url);
    toast.success('Assessment link copied to clipboard!');
  };

  if (loading) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: 'hsl(var(--muted-foreground))' }}>Loading assessments...</div>;
  }

  return (
    <div data-testid="assessment-list-page">
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button onClick={() => navigate(`/jobs/${jobId}`)} style={{ background: 'none', border: 'none', color: 'hsl(var(--muted-foreground))', cursor: 'pointer', padding: '0.25rem' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <div>
            <h1 className="page-title">Skill Assessments</h1>
            <p className="page-subtitle">{assessments.length} assessment{assessments.length !== 1 ? 's' : ''} created</p>
          </div>
        </div>
        <Button
          data-testid="assessment-builder-generate-button"
          onClick={handleGenerate}
          disabled={generating}
          style={{ background: '#0076A3', color: '#fff', borderRadius: 999, padding: '0.625rem 1.25rem', fontWeight: 600 }}
        >
          {generating ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span className="status-dot status-processing" /> Generating with AI...
            </span>
          ) : (
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Generate Assessment
            </span>
          )}
        </Button>
      </div>

      {assessments.length === 0 ? (
        <div className="empty-state" style={{ paddingTop: '4rem' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ width: 56, height: 56, marginBottom: '1.5rem' }}>
            <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
            <path d="M9 14l2 2 4-4"/>
          </svg>
          <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: '#002A38', marginBottom: '0.5rem' }}>No assessments yet</h3>
          <p style={{ marginBottom: '1.5rem' }}>Generate an AI-powered skill assessment based on the job requirements.</p>
          <Button onClick={handleGenerate} disabled={generating} style={{ background: '#0076A3', color: '#fff', borderRadius: 999, padding: '0.625rem 1.25rem', fontWeight: 600 }}>
            {generating ? 'Generating...' : 'Generate First Assessment'}
          </Button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {assessments.map((a) => (
            <Card key={a._id} style={{ background: 'hsl(var(--card))', border: `1px solid ${a.status === 'generating' ? '#FDE68A' : 'hsl(var(--border))'}`, borderRadius: 14, padding: '1.5rem' }}>
              {a.status === 'generating' ? (
                <div style={{ textAlign: 'center', padding: '1.5rem 0' }}>
                  <span className="status-dot status-processing" style={{ display: 'inline-block', width: 12, height: 12, marginBottom: '0.75rem' }} />
                  <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.0625rem', fontWeight: 600, color: '#92400E', marginBottom: '0.375rem' }}>AI is generating your assessment...</h3>
                  <p style={{ fontSize: '0.8125rem', color: '#A16207' }}>Questions are generated, then every answer key is independently re-solved and verified. This usually takes 45-90 seconds. The page will auto-refresh.</p>
                </div>
              ) : (
              <div>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                <div>
                  <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.0625rem', fontWeight: 600, marginBottom: '0.25rem' }}>{a.title}</h3>
                  <p style={{ fontSize: '0.8125rem', color: 'hsl(var(--muted-foreground))' }}>{a.description}</p>
                </div>
                <Badge style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#059669', border: '1px solid rgba(16, 185, 129, 0.3)', borderRadius: 999 }}>{a.status}</Badge>
              </div>

              <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1rem', fontSize: '0.8125rem', color: 'hsl(var(--muted-foreground))' }}>
                <span>{(a.questions || []).length} questions</span>
                <span>{a.total_points} points</span>
                <span>{a.time_limit_minutes} min time limit</span>
                <span>{a.attempt_count || 0} attempts</span>
              </div>

              {a.key_verification?.mcq_count > 0 && (
                <div
                  data-testid="assessment-key-verification"
                  style={{
                    display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem',
                    marginBottom: '1rem', fontSize: '0.6875rem',
                  }}
                >
                  <span
                    data-testid="assessment-keys-verified"
                    style={{ padding: '0.1875rem 0.5rem', borderRadius: 999, background: 'rgba(16, 185, 129, 0.12)', color: '#059669', border: '1px solid rgba(16, 185, 129, 0.3)' }}
                  >
                    {a.key_verification.verified}/{a.key_verification.mcq_count} answer keys independently verified
                  </span>
                  {a.key_verification.disputed > 0 && (
                    <span
                      data-testid="assessment-keys-disputed"
                      title="A second model chose a different answer, so both answers are accepted when grading."
                      style={{ padding: '0.1875rem 0.5rem', borderRadius: 999, background: 'rgba(245, 158, 11, 0.12)', color: '#D97706', border: '1px solid rgba(245, 158, 11, 0.3)' }}
                    >
                      {a.key_verification.disputed} ambiguous &middot; double-keyed
                    </span>
                  )}
                  {a.key_verification.repaired > 0 && (
                    <span
                      data-testid="assessment-keys-repaired"
                      style={{ padding: '0.1875rem 0.5rem', borderRadius: 999, background: 'rgba(240, 80, 0, 0.1)', color: '#0076A3', border: '1px solid rgba(240, 80, 0, 0.25)' }}
                    >
                      {a.key_verification.repaired} key auto-corrected
                    </span>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem', marginBottom: '1rem' }}>
                {[...new Set((a.questions || []).map(q => q.skill))].map(skill => (
                  <span key={skill} style={{ padding: '0.1875rem 0.5rem', borderRadius: 999, background: 'rgba(240, 80, 0, 0.1)', border: '1px solid rgba(240, 80, 0, 0.2)', fontSize: '0.6875rem', color: '#0076A3' }}>{skill}</span>
                ))}
              </div>

              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <Button variant="outline" onClick={() => navigate(`/assessments/${a._id}`)} style={{ borderRadius: 10, fontSize: '0.8125rem' }}>View Details</Button>
                <Button variant="outline" onClick={() => navigate(`/assessments/${a._id}/results`)} style={{ borderRadius: 10, fontSize: '0.8125rem' }}>View Results ({a.attempt_count || 0})</Button>
                <Button onClick={() => copyLink(a.access_code)} style={{ background: '#0076A3', color: '#fff', borderRadius: 999, fontSize: '0.8125rem', fontWeight: 600 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>
                  Copy Link
                </Button>
              </div>
              </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
